import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Mail, CheckCircle2, Clock, ShieldCheck, ChevronDown, ChevronUp, ArrowRight, MessageSquare } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button, TextArea, EmptyState } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";

const STATUS_TONE = {
  "Startup Response Pending": "warn",
  "Response Under Review": "muted",
  "Response Shared": "success",
};

function QueryRow({ q, draft, setDraft, onSubmit, submitting, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const isPending = q.status === "Startup Response Pending";
  return (
    <Card testId={`query-${q.query_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="overline flex items-center gap-2 flex-wrap">
            <MessageSquare size={11} />
            <span>{q.investor_display}</span>
            {q.investor_type && <span className="text-ink-faint font-mono lowercase">· {q.investor_type}</span>}
          </div>
          <div className="text-sm text-ink-soft mt-1">
            {q.created_at ? new Date(q.created_at).toLocaleString() : ""} · {(q.questions || []).length} question(s)
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Pill tone={STATUS_TONE[q.status] || "muted"} testId={`query-status-${q.query_id}`}>
            {isPending && <Clock size={10} className="inline mr-1" />}
            {q.status === "Response Under Review" && <ShieldCheck size={10} className="inline mr-1" />}
            {q.status === "Response Shared" && <CheckCircle2 size={10} className="inline mr-1" />}
            {q.status}
          </Pill>
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-xs font-mono uppercase tracking-widest text-[#8A6600] hover:underline flex items-center gap-1"
            data-testid={`query-toggle-${q.query_id}`}
          >
            {open ? <>Hide <ChevronUp size={11} /></> : <>Open <ChevronDown size={11} /></>}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-4 space-y-3">
          <ol className="list-decimal ml-4 space-y-2 text-sm">
            {(q.questions || []).map((qq, i) => (
              <li key={qq.question_id || i} className="text-ink" data-testid={`query-q-${q.query_id}-${i}`}>{qq.question || qq.text}</li>
            ))}
          </ol>

          {q.answer ? (
            <div className="mt-2 pl-3 border-l-2 border-emerald-500/50 bg-emerald-50/40 rounded-r px-3 py-2" data-testid={`query-response-${q.query_id}`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-1 flex items-center gap-1">
                <CheckCircle2 size={10} /> Your response · {q.responded_at ? new Date(q.responded_at).toLocaleDateString() : "—"}
              </div>
              <div className="text-sm text-ink whitespace-pre-wrap leading-relaxed">{q.answer}</div>
              {q.status === "Response Under Review" && (
                <div className="text-[11px] font-mono text-ink-soft mt-2 italic">
                  Awaiting VenturaZ review before it reaches the investor.
                </div>
              )}
            </div>
          ) : (
            <div className="pt-2 border-t border-navy/10 space-y-2">
              <TextArea
                label="Compose your response"
                rows={4}
                value={draft || ""}
                onChange={(e) => setDraft(q.query_id, e.target.value)}
                testId={`respond-textarea-${q.query_id}`}
                placeholder="Write a clear, factual response. VenturaZ will review before sharing with the investor."
              />
              <div className="flex items-center gap-2">
                <Button onClick={() => onSubmit(q)} disabled={submitting || !(draft || "").trim()} testId={`respond-btn-${q.query_id}`}>
                  {submitting ? "Submitting…" : "Submit Response"}
                </Button>
                <span className="text-[11px] font-mono text-ink-soft">
                  Reviewed by VenturaZ · shared with investor after approval
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function StartupQueries() {
  const [queries, setQueries] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/startup/queries");
      setQueries(data || []);
    } catch (e) { toast.error("Failed to load queries"); }
    finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, []);

  const setDraft = (id, val) => setDrafts((d) => ({ ...d, [id]: val }));
  const submit = async (q) => {
    const answer = (drafts[q.query_id] || "").trim();
    if (!answer) return toast.error("Please write a response first.");
    setBusy((b) => ({ ...b, [q.query_id]: true }));
    try {
      await api.post("/startup/queries/respond", { query_id: q.query_id, answer, confidential: false });
      toast.success("Response submitted — VenturaZ will review before sharing.");
      setDrafts((d) => ({ ...d, [q.query_id]: "" }));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy((b) => ({ ...b, [q.query_id]: false })); }
  };

  const [pending, awaitingReview, shared] = useMemo(() => {
    const p = [], r = [], s = [];
    queries.forEach((q) => {
      if (q.status === "Startup Response Pending") p.push(q);
      else if (q.status === "Response Under Review") r.push(q);
      else s.push(q);
    });
    return [p, r, s];
  }, [queries]);

  return (
    <DashboardShell title="Investor Questions" subtitle="STARTUP · YOUR INVESTOR INBOX">
      <Toaster richColors position="top-right" />

      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        <Card testId="queries-stat-pending">
          <div className="flex items-center gap-2"><Clock size={13} className="text-amber-600" /><div className="overline">Awaiting your reply</div></div>
          <div className="font-heading text-2xl text-ink mt-1">{pending.length}</div>
        </Card>
        <Card testId="queries-stat-review">
          <div className="flex items-center gap-2"><ShieldCheck size={13} className="text-[#8A6600]" /><div className="overline">Under VenturaZ review</div></div>
          <div className="font-heading text-2xl text-ink mt-1">{awaitingReview.length}</div>
        </Card>
        <Card testId="queries-stat-shared">
          <div className="flex items-center gap-2"><CheckCircle2 size={13} className="text-emerald-600" /><div className="overline">Shared with investor</div></div>
          <div className="font-heading text-2xl text-ink mt-1">{shared.length}</div>
        </Card>
      </div>

      {!loaded ? (
        <div className="text-sm text-ink-soft italic">Loading queries…</div>
      ) : queries.length === 0 ? (
        <EmptyState
          title="No queries yet"
          hint="Investors' questions arrive here as soon as they're sent. You'll get a notification when one lands."
        />
      ) : (
        <div className="space-y-6">
          {pending.length > 0 && (
            <section data-testid="queries-section-pending">
              <div className="text-xs font-mono uppercase tracking-widest text-amber-700 mb-2 flex items-center gap-1">
                <Clock size={11} /> Awaiting your reply ({pending.length})
              </div>
              <div className="space-y-3">
                {pending.map((q) => (
                  <QueryRow
                    key={q.query_id} q={q}
                    draft={drafts[q.query_id]}
                    setDraft={setDraft}
                    onSubmit={submit}
                    submitting={!!busy[q.query_id]}
                    defaultOpen={true}
                  />
                ))}
              </div>
            </section>
          )}
          {awaitingReview.length > 0 && (
            <section data-testid="queries-section-review">
              <div className="text-xs font-mono uppercase tracking-widest text-[#8A6600] mb-2 flex items-center gap-1">
                <ShieldCheck size={11} /> Under VenturaZ review ({awaitingReview.length})
              </div>
              <div className="space-y-3">
                {awaitingReview.map((q) => (
                  <QueryRow key={q.query_id} q={q} draft={drafts[q.query_id]} setDraft={setDraft} onSubmit={submit} submitting={!!busy[q.query_id]} defaultOpen={false} />
                ))}
              </div>
            </section>
          )}
          {shared.length > 0 && (
            <section data-testid="queries-section-shared">
              <div className="text-xs font-mono uppercase tracking-widest text-emerald-700 mb-2 flex items-center gap-1">
                <CheckCircle2 size={11} /> Shared with investor ({shared.length})
              </div>
              <div className="space-y-3">
                {shared.map((q) => (
                  <QueryRow key={q.query_id} q={q} draft={drafts[q.query_id]} setDraft={setDraft} onSubmit={submit} submitting={!!busy[q.query_id]} defaultOpen={false} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </DashboardShell>
  );
}
